import React, { useCallback, useMemo, useRef, useState } from 'react'
import { Modal, message } from 'antd'

import { terminateEmployeeVcnCards } from '../../api/vcn-management-api-client'
import CardActionBar, {
  CARD_ACTION_LABELS,
  CardActionKind,
} from './CardActionBar'
import { showCardActionOutcome } from './cardActionOutcome'
import { VcnCardStatus } from './types'
import type { VcnManagementCardRow } from './types'
import { useInvalidateVcnData } from './hooks/useInvalidateVcnData'

export interface VcnManagementCardActionsProps {
  selectedRows: VcnManagementCardRow[]
  /** Called after a completed action, to drop the selection it acted on. */
  onClearSelection: () => void
  /** True while the list is refetching — acting on stale cached rows is blocked. */
  isFetching: boolean
}

/** Names a card in the partial-failure list: the two fields an admin can see in the grid. */
function cardLabel(
  row: VcnManagementCardRow | undefined,
  fallback: string
): string {
  if (!row) return fallback
  const person = row.insuredPersonName ?? 'Unknown holder'
  return row.lastFourDigits ? `${person} ····${row.lastFourDigits}` : person
}

/**
 * Keys a card by provider AND token: the two providers' key spaces are separate
 * (WEX merchant_log_id vs MARQETA card_public_key), so the token alone is not
 * an identity. Typed loosely because the response echoes `provider` as a plain
 * string while a row carries the enum.
 */
function providerAndKey(card: {
  provider: string
  virtualCardKey: string
}): string {
  return `${card.provider}:${card.virtualCardKey}`
}

/**
 * Card Mode's action policy. Terminate only — deliberately.
 *
 * `cards/terminate` is the one card-keyed endpoint: it acts on the
 * `virtualCardKey` sent, which is the key the ticked row IS. Terminate & New and
 * Create go through the benefit-keyed endpoints, which resolve the benefit's
 * primary card — so ticking a non-primary in-use card and pressing either would
 * act on a sibling. On a page whose whole premise is "one row is one card", that
 * is not a button worth offering. Those two stay in the "Associated VCNs" panel,
 * where the card is chosen in the context of its benefit.
 *
 * The chrome is shared with that panel ({@link CardActionBar}); only this policy
 * is per-surface.
 */
export function VcnManagementCardActions({
  selectedRows,
  onClearSelection,
  isFetching,
}: VcnManagementCardActionsProps) {
  const [activeAction, setActiveAction] = useState<CardActionKind | null>(null)
  const invalidateVcnData = useInvalidateVcnData()

  // An already-terminated card has nothing to terminate. Card Mode can filter
  // to Terminated and select those, so the selection is filtered rather than
  // just gated — sending them would return per-card failures for cards the
  // admin never meant to act on.
  const terminableRows = useMemo(
    () =>
      selectedRows.filter((row) => row.cardStatus !== VcnCardStatus.TERMINATED),
    [selectedRows]
  )

  // Modal.confirm freezes its onOk closure when it opens, so read the live list
  // through a ref: a refetch that prunes the selection while the dialog is open
  // is then honored on confirm.
  const terminableRowsRef = useRef(terminableRows)
  terminableRowsRef.current = terminableRows

  const runTerminate = useCallback(async (): Promise<void> => {
    const actionLabel = CARD_ACTION_LABELS[CardActionKind.TERMINATE]
    const cards = terminableRowsRef.current
    // Reject so Modal.confirm stays open — a silent resolve would close the
    // dialog and look like the destructive action succeeded.
    if (cards.length === 0) {
      void message.warning(
        `No eligible cards left to ${actionLabel}. Selection may have changed — review and try again.`
      )
      throw new Error('No eligible cards to terminate')
    }
    setActiveAction(CardActionKind.TERMINATE)
    try {
      const result = await terminateEmployeeVcnCards(
        cards.map((row) => ({
          provider: row.provider,
          virtualCardKey: row.virtualCardKey,
        }))
      )
      const rowByKey = new Map(cards.map((row) => [providerAndKey(row), row]))
      showCardActionOutcome(actionLabel, {
        successCount: result.successCount,
        failedCount: result.failedCount,
        failureLines: result.results
          .filter((item) => !item.success)
          .map(
            (item) =>
              `${cardLabel(rowByKey.get(providerAndKey(item)), item.virtualCardKey)}: ${
                item.errorMessage ?? 'unknown error'
              }`
          ),
      })
      onClearSelection()
      // Not awaited: TanStack Query can leave that promise pending when
      // refetches are paused (offline), which would stick activeAction and keep
      // the bar disabled. A terminated card changes what BOTH tabs list, hence
      // the shared invalidation rather than naming query keys here.
      invalidateVcnData()
    } catch (error) {
      void message.error(`${actionLabel} failed. Please try again.`)
      // Rethrow so Modal.confirm keeps the dialog open on failure.
      throw error
    } finally {
      setActiveAction(null)
    }
  }, [invalidateVcnData, onClearSelection])

  const handleTerminate = useCallback(() => {
    const skippedCount = selectedRows.length - terminableRows.length
    Modal.confirm({
      title: `${CARD_ACTION_LABELS[CardActionKind.TERMINATE]} ${terminableRows.length} card(s)?`,
      content: (
        <div>
          This deactivates the selected virtual cards. This cannot be undone.
          {skippedCount > 0 ? (
            <p style={{ marginTop: 8 }}>
              {skippedCount} already-terminated card(s) in the selection will be
              skipped.
            </p>
          ) : null}
        </div>
      ),
      okText: CARD_ACTION_LABELS[CardActionKind.TERMINATE],
      okButtonProps: { danger: true },
      onOk: runTerminate,
    })
  }, [runTerminate, selectedRows.length, terminableRows.length])

  return (
    <CardActionBar
      selectedCount={selectedRows.length}
      activeAction={activeAction}
      actions={[
        {
          kind: CardActionKind.TERMINATE,
          danger: true,
          disabled: terminableRows.length === 0 || isFetching,
          onClick: handleTerminate,
        },
      ]}
      onClose={onClearSelection}
    />
  )
}
